/**
 * Gets a field value from the record, supporting nested paths like "user.firstName"
 */
function getFieldValue(
	record: Record<string, unknown>,
	fieldRef: string,
): unknown {
	if (fieldRef.includes('.')) {
		const parts = fieldRef.split('.');

		// Try to access nested path first (e.g., user.firstName)
		let current: unknown = record;

		for (const part of parts) {
			if (current != null && typeof current === 'object' && part in current) {
				current = (current as Record<string, unknown>)[part];
			} else {
				current = undefined;
				break;
			}
		}

		// If nested access worked, use that value
		if (current !== undefined) {
			return current;
		}

		// Fallback: try just the last part for backwards compatibility
		const fieldName: string = parts[parts.length - 1] as string;

		return record[fieldName];
	}

	// Direct field reference
	return record[fieldRef];
}

/**
 * Converts a value to string for template output
 */
function valueToString(value: unknown): string {
	if (value === null || value === undefined) {
		return '';
	}

	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'number') {
		return value.toString();
	}

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	if (value instanceof Date) {
		return value.toLocaleDateString();
	}

	// For objects or arrays, return empty string to avoid [object Object]
	if (typeof value === 'object') {
		return '';
	}

	// Fallback: convert to string
	// eslint-disable-next-line @typescript-eslint/no-base-to-string
	return String(value);
}

/**
 * Checks if a value is considered "present" (non-null and non-empty string)
 */
function hasValue(value: unknown): boolean {
	return value != null && !(typeof value === 'string' && value.trim() === '');
}

/**
 * Parses and evaluates a value mapping expression like "field:val1=out1,val2=out2"
 * Returns the mapped output or empty string if no match
 */
function evaluateValueMapping(
	expression: string,
	record: Record<string, unknown>,
): { result: string; hasValue: boolean } {
	const colonIndex = expression.indexOf(':');

	if (colonIndex === -1) {
		return { result: '', hasValue: false };
	}

	const fieldPart = expression.substring(0, colonIndex).trim();
	const mappingPart = expression.substring(colonIndex + 1);

	// Get the field value
	const fieldValue = getFieldValue(record, fieldPart);
	const fieldValueStr = valueToString(fieldValue).toLowerCase().trim();

	// Parse mappings: "val1=out1,val2=out2"
	const mappings = mappingPart.split(',');

	for (const mapping of mappings) {
		const eqIndex = mapping.indexOf('=');

		if (eqIndex === -1) {
			continue;
		}

		const matchValue = mapping.substring(0, eqIndex).trim().toLowerCase();
		const outputValue = mapping.substring(eqIndex + 1).trim();

		if (fieldValueStr === matchValue) {
			return { result: outputValue, hasValue: true };
		}
	}

	return { result: '', hasValue: false };
}

/**
 * Parses and evaluates a fallback expression like "field1|field2|\"literal\""
 * Returns the first non-null value
 */
function evaluateFallback(
	expression: string,
	record: Record<string, unknown>,
): { result: string; hasValue: boolean } {
	const parts = expression.split('|');

	for (const part of parts) {
		const trimmed = part.trim();

		// Check for literal string (quoted)
		if (
			(trimmed.startsWith('"') && trimmed.endsWith('"')) ||
			(trimmed.startsWith("'") && trimmed.endsWith("'"))
		) {
			const literal = trimmed.slice(1, -1);

			return { result: literal, hasValue: literal.length > 0 };
		}

		// It's a field reference
		const value = getFieldValue(record, trimmed);

		if (hasValue(value)) {
			return { result: valueToString(value), hasValue: true };
		}
	}

	return { result: '', hasValue: false };
}

/**
 * Processes a single template expression (content inside {})
 * Supports:
 * - Simple field: {fieldName}
 * - Nested field: {object.fieldName}
 * - Fallback: {field1|field2|"default"}
 * - Value mapping: {field:value1=output1,value2=output2}
 */
function processExpression(
	expression: string,
	record: Record<string, unknown>,
): { result: string; hasValue: boolean } {
	// Check for value mapping (contains : but not as part of nested path like http:)
	// Value mapping format: field:val1=out1,val2=out2
	if (expression.includes(':') && expression.includes('=')) {
		return evaluateValueMapping(expression, record);
	}

	// Check for fallback chain (contains |)
	if (expression.includes('|')) {
		return evaluateFallback(expression, record);
	}

	// Simple field reference
	const value = getFieldValue(record, expression);

	return { result: valueToString(value), hasValue: hasValue(value) };
}

/**
 * Safely evaluates a template string by replacing field placeholders with actual values
 *
 * Supported syntax:
 * - {fieldName} - Simple field reference
 * - {object.fieldName} - Nested field reference
 * - {field1|field2|"default"} - Fallback chain (uses first non-null value)
 * - {field:val1=out1,val2=out2} - Value mapping (outputs mapped value based on field)
 *
 * Examples:
 * - "{firstName} {lastName}" → "John Doe"
 * - "{externalId|id}" → "ABC123" (or numeric id if externalId is null)
 * - "{leadOrCustomer:Customer=C,Lead=L}#{externalId|id}" → "C#ABC123" or "L#456"
 *
 * @param template The template string containing {fieldName} placeholders
 * @param record The data record containing field values
 * @param defaultValue Optional value to return if all template variables are empty
 * @returns The evaluated string with placeholders replaced by actual values, or defaultValue if all variables are empty
 */
export function evaluateTemplate(
	template: string | null,
	record: Record<string, unknown> | null,
	defaultValue?: string,
): string {
	// noinspection SuspiciousTypeOfGuard
	if (template == null || typeof template !== 'string') {
		return '';
	}

	if (record == null || typeof record !== 'object') {
		return template;
	}

	// Track if all variables are empty (for defaultValue logic)
	let allVariablesEmpty: boolean = true;

	// Replace each {expression} with the evaluated result
	// Updated regex to capture everything inside braces (except nested braces)
	const result = template.replace(/\{([^}]+)\}/g, (_, expression: string) => {
		const { result: evalResult, hasValue: exprHasValue } = processExpression(
			expression,
			record,
		);

		if (exprHasValue) {
			allVariablesEmpty = false;
		}

		return evalResult;
	});

	// If all variables were empty and defaultValue is provided, return defaultValue
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (allVariablesEmpty && defaultValue != null) {
		return defaultValue;
	}

	return result;
}

/**
 * Creates a preview of the template with sample data
 * @param template The template string
 * @param availableFields Fields with their display names and table info
 * @returns A preview string showing how the template would look
 */
export function createTemplatePreview(
	template: string | null,
	availableFields: Array<{
		fieldName: string;
		fieldLabel: string;
		objectTable: string;
		objectLabel: string;
	}>,
): string {
	if (template == null) {
		return '';
	}

	// Create sample data based on field names (using just the field name as key since that's what the record will have)
	const sampleData: Record<string, string> = {};

	availableFields.forEach((field) => {
		const fieldName = field.fieldName.toLowerCase();
		const label = field.fieldLabel;

		// Generate contextual sample data based on field name
		if (fieldName.includes('city')) {
			sampleData[field.fieldName] = 'San Francisco';
		} else if (fieldName.includes('state')) {
			sampleData[field.fieldName] = 'CA';
		} else if (fieldName.includes('zip') || fieldName.includes('postal')) {
			sampleData[field.fieldName] = '94105';
		} else if (fieldName.includes('address') || fieldName.includes('street')) {
			sampleData[field.fieldName] = '123 Main St';
		} else if (fieldName.includes('name')) {
			if (fieldName.includes('first')) {
				sampleData[field.fieldName] = 'John';
			} else if (fieldName.includes('last')) {
				sampleData[field.fieldName] = 'Doe';
			} else if (fieldName.includes('company')) {
				sampleData[field.fieldName] = 'Acme Corp';
			} else {
				sampleData[field.fieldName] = 'Sample Name';
			}
		} else if (fieldName.includes('email')) {
			sampleData[field.fieldName] = 'example@email.com';
		} else if (fieldName.includes('phone')) {
			sampleData[field.fieldName] = '(555) 123-4567';
		} else if (fieldName.includes('date')) {
			sampleData[field.fieldName] = new Date().toLocaleDateString();
		} else if (fieldName.includes('amount') || fieldName.includes('price')) {
			sampleData[field.fieldName] = '$100.00';
		} else if (fieldName.includes('quantity') || fieldName.includes('count')) {
			sampleData[field.fieldName] = '5';
		} else {
			// Default sample based on label
			sampleData[field.fieldName] = `Sample ${label}`;
		}
	});

	return evaluateTemplate(template, sampleData);
}
